import { Link, Outlet } from "@tanstack/react-router";
import { Building2, CreditCard, LogOut, Menu, Settings, User } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import activateurLogo from "@/assets/logo-activateur.svg";
import { Nav } from "@/app/Nav";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { Button } from "@/components/ui/button";
import { Sheet } from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "@/components/ui/sonner";
import { getOnBehalfOf } from "@/lib/api/on-behalf";
import { useAuth } from "@/lib/auth/auth-context";
import { isExpert, showBilling } from "@/lib/auth/roles";

/**
 * Authenticated app shell: header (brand, role-conditional nav, language
 * switcher, user menu) + the routed content. A skip link and a single <main>
 * landmark keep keyboard/screen-reader navigation sane.
 */
export function AppShell({ children }: { children?: React.ReactNode }) {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const canSeeBilling = showBilling(user);
  // The enterprise profile link only makes sense when there's a profile to edit.
  // Resolve it the same way EnterpriseProfilePage does — the on-behalf-of member's
  // profile in partner mode, else the user's own — so the link is shown iff the
  // page has something to open (experts / partners without an enterprise: none).
  const hasEnterpriseProfile = Boolean(
    getOnBehalfOf() ?? user?.enterprise_profile_id,
  );
  // Experts have no header links, so there's nothing to open on mobile.
  const hasNav = !isExpert(user);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // `contact_email`, not `email`: for a training participant the latter holds a
  // fabricated identifier on a non-routable domain, and this label is rendered
  // in the account menu on every page. The Django account menu resolves the
  // same way through `User.display_identity`.
  const displayName =
    [user?.first_name, user?.last_name].filter(Boolean).join(" ") ||
    user?.contact_email ||
    "";

  function handleLogout() {
    logout();
    toast.success(t("auth.loggedOut"));
  }

  return (
    <div className="flex min-h-screen flex-col">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-background focus:px-4 focus:py-2 focus:ring-2 focus:ring-ring"
      >
        {t("shell.skipToContent")}
      </a>

      {/* Thin four-theme spectrum: the app's identity mark, on every page. */}
      <div
        aria-hidden
        className="h-1 bg-gradient-to-r from-theme-governance via-theme-economic to-theme-environment"
      />
      <header className="border-b">
        <div className="container flex h-16 items-center gap-2 sm:gap-4">
          {hasNav && (
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              aria-label={t("shell.openMenu")}
              aria-expanded={mobileNavOpen}
              onClick={() => setMobileNavOpen(true)}
            >
              <Menu aria-hidden />
            </Button>
          )}
          {/* The logo is the only elastic item in the bar — at its natural
              height it is 193px wide, which together with the menu toggle and
              the two controls pushed the header past a 375px viewport. Letting
              it shrink (and scale inside its box) keeps the bar within the
              container at any width. */}
          <Link to="/dashboard" aria-label={t("shell.appName")} className="min-w-0">
            <img
              src={activateurLogo}
              alt={t("shell.appName")}
              className="h-8 w-auto max-w-full object-contain object-left"
            />
          </Link>
          <div className="hidden flex-1 md:block">
            <Nav />
          </div>
          {/* Push the controls to the right on mobile (where <Nav> is hidden);
              on md+ the flex-1 <Nav> already fills the gap. */}
          <div className="ml-auto flex items-center gap-1 sm:gap-2 md:ml-0">
            <LanguageSwitcher />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" aria-label={t("shell.userMenu")}>
                  <User aria-hidden />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {displayName && <DropdownMenuLabel>{displayName}</DropdownMenuLabel>}
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/settings">
                    <Settings aria-hidden />
                    {t("nav.settings")}
                  </Link>
                </DropdownMenuItem>
                {canSeeBilling && (
                  <DropdownMenuItem asChild>
                    <Link to="/billing">
                      <CreditCard aria-hidden />
                      {t("nav.billing")}
                    </Link>
                  </DropdownMenuItem>
                )}
                {hasEnterpriseProfile && (
                  <DropdownMenuItem asChild>
                    <Link to="/enterprise-profile">
                      <Building2 aria-hidden />
                      {t("nav.enterpriseProfile")}
                    </Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onSelect={handleLogout}>
                  <LogOut aria-hidden />
                  {t("nav.logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {hasNav && mobileNavOpen && (
        <Sheet title={t("shell.menu")} onClose={() => setMobileNavOpen(false)}>
          <Nav orientation="vertical" onNavigate={() => setMobileNavOpen(false)} />
        </Sheet>
      )}

      <main id="main-content" className="container flex-1 py-8">
        {children ?? <Outlet />}
      </main>
    </div>
  );
}
